import * as React from "react";
import { Link, useNavigate } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { PAGE_COUNT } from "@/lib/types";

interface QuranPagePaginationProps {
  pageNumber: number;
}

const arrowStyles =
  "pressable grid size-11 place-items-center rounded-full text-ink-muted hover:bg-paper-200 hover:text-ink";
// Same footprint at the first/last page, so the pill never changes width.
const arrowDisabledStyles =
  "grid size-11 place-items-center rounded-full text-ink-muted opacity-35";

// The mushaf reads right-to-left: the next page is on the left.
const QuranPagePagination: React.FC<QuranPagePaginationProps> = ({
  pageNumber,
}) => {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [value, setValue] = React.useState(String(pageNumber));

  React.useEffect(() => setValue(String(pageNumber)), [pageNumber]);

  // A typed page number is a jump, not a turn: it stays instant (no view
  // transition). It commits on blur, so Enter just blurs the field: one
  // navigation, the phone keyboard closes and the arrow keys turn pages again.
  const commit = () => {
    const target = Number(value);
    if (Number.isInteger(target) && target >= 1 && target <= PAGE_COUNT) {
      if (target !== pageNumber) navigate(`/${target}`);
    } else {
      setValue(String(pageNumber));
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (document.activeElement === inputRef.current) inputRef.current?.blur();
    else commit();
  };

  return (
    <div className="pointer-events-none sticky bottom-0 z-30 flex justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 print:hidden">
      <div
        // Captured on its own during a page turn, so it stays above the
        // turning page instead of disappearing under its snapshot.
        style={{ viewTransitionName: "mushaf-pagination" }}
        className="pointer-events-auto flex items-center gap-1 rounded-full bg-paper-50/85 p-1 shadow-float ring-1 ring-paper-300/80 backdrop-blur-md"
      >
        {pageNumber < PAGE_COUNT ? (
          <Link
            to={`/${pageNumber + 1}`}
            viewTransition
            className={arrowStyles}
            aria-label="Sljedeća stranica"
            title="Sljedeća stranica (←)"
          >
            <ChevronLeft className="size-5" />
          </Link>
        ) : (
          <span
            role="link"
            aria-disabled="true"
            aria-label="Sljedeća stranica"
            className={arrowDisabledStyles}
          >
            <ChevronLeft className="size-5" />
          </span>
        )}

        {/* noValidate: out-of-range input is reset by commit(), not blocked
            by the browser's (English) validation bubble. */}
        <form noValidate onSubmit={submit}>
          <label className="flex items-center gap-1.5 px-1 text-sm text-ink-subtle">
            <input
              ref={inputRef}
              type="number"
              inputMode="numeric"
              enterKeyHint="go"
              min={1}
              max={PAGE_COUNT}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              onBlur={commit}
              aria-label="Broj stranice"
              className="h-9 w-14 rounded-full border-0 bg-paper-200/70 p-0 text-center text-base font-semibold tabular-nums text-ink [appearance:textfield] focus:ring-2 focus:ring-emerald-700 sm:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <span aria-hidden className="pr-1 tabular-nums">
              / {PAGE_COUNT}
            </span>
          </label>
        </form>

        {pageNumber > 1 ? (
          <Link
            to={`/${pageNumber - 1}`}
            viewTransition
            className={arrowStyles}
            aria-label="Prethodna stranica"
            title="Prethodna stranica (→)"
          >
            <ChevronRight className="size-5" />
          </Link>
        ) : (
          <span
            role="link"
            aria-disabled="true"
            aria-label="Prethodna stranica"
            className={arrowDisabledStyles}
          >
            <ChevronRight className="size-5" />
          </span>
        )}
      </div>
    </div>
  );
};

export default React.memo(QuranPagePagination);
